import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Pin, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, StatusDot } from '../../components/ui';
import { useAgents, useConversation, useModels, useProjects, type ProjectDetail } from '../../lib/queries';

/** Top-bar PROJECT dropdown: switch project from anywhere in the workspace. */
export function ProjectPicker({ project }: { project: ProjectDetail }) {
  const nav = useNavigate();
  const { data: projects = [] } = useProjects();
  const [open, setOpen] = useState(false);
  const go = (path: string) => {
    setOpen(false);
    nav(path);
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button className="dropdown-trigger" aria-label={`Project: ${project.name}. Switch project`}>
            <StatusDot status={project.server?.status} />
            <span className="label">{project.name}</span>
            <ChevronDown className="icon faint" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className="menu" align="end" sideOffset={8} style={{ width: 280 }}>
            <div className="micro menu-head">Switch project</div>
            {projects
              .filter((p) => !p.archived)
              .map((p) => (
                <button key={p.id} className="menu-item" data-active={p.id === project.id} onClick={() => go(`/p/${p.slug}`)}>
                  <StatusDot status={p.server?.status} />
                  <span className="ellipsis" style={{ flex: 1 }}>
                    {p.name}
                  </span>
                  {p.runningTurns > 0 && <Spinner className="accent-text" />}
                  {p.pinned && <Pin className="icon" style={{ color: 'var(--color-accent)', fontSize: 12 }} />}
                </button>
              ))}
            <div className="menu-foot">
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => go('/')}>
                All projects
              </button>
              <button className="btn btn-ghost accent" style={{ flex: 1 }} onClick={() => go('/new')}>
                <Plus className="icon" />
                New project
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
    </Popover.Root>
  );
}

/** The model the next turn will use: the conversation's override, else the active agent's model. */
export function useNextModel(project: ProjectDetail, convId?: string) {
  const conv = useConversation(convId);
  const { data: agents = [] } = useAgents(project.id);
  const { data: models = [] } = useModels();
  const defaultAgent = agents.find((a) => a.isDefault) ?? agents[0];
  const activeAgent = agents.find((a) => a.id === conv.data?.activeAgentId) ?? defaultAgent;
  const model = models.find((m) => m.id === (conv.data?.nextModelId ?? activeAgent?.modelId));
  return { model, models, activeAgent, defaultAgent, conv };
}
